import { useMemo } from 'react';

import { useParams, useQuery } from '../../../contexts/RouterContext';
import type { SnapshotFilters } from '../../../../shared/issuesV2/snapshots';
import { applyFilterDefaults } from '../../../../shared/issuesV2/snapshots';

const parserFn = (store: Record<string, any>): SnapshotFilters => {
  let teamId: SnapshotFilters['teamId'];
  // teamId is an array of numbers in this case
  if (store.teamId && store.teamId !== 'null') {
    teamId = (store.teamId ?? []).map((i: any) => Number(i));
  } else {
    teamId = store.teamId;
  }

  return applyFilterDefaults({
    labels: (store.labels ?? []).map((i: any) => Number(i)),
    start: store.start ? new Date(store.start) : undefined,
    end: store.end ? new Date(store.end) : undefined,
    category: store.category,
    teamId,
  });
};

/**
 * Hook for standardizing access to `useQuery` for the Issue Overview page. Lets us avoid
 * duplicating the `parserFn` passed in to `useQuery`.
 */
const useIssueOverview = () => {
  const [filters, setFilters] = useQuery<SnapshotFilters>(parserFn);

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const params = useParams<{ project_id?: string }>();

  const isProjectPage = !!params.project_id;

  // Not memoizing this causes infinite rerendering.
  const filledFilters = useMemo(() => {
    return {
      ...filters,
      // If we have a params.project_id, that means we're viewing this from a Project page and not the global page.
      projectId: params.project_id,
      // We don't want to provide teamId on the project page.
      teamId: !isProjectPage ? filters.teamId : undefined,
    };
  }, [filters, params.project_id, isProjectPage]);

  return {
    filters: filledFilters,
    setFilters,
    isProjectPage,
  };
};

export default useIssueOverview;
